import React from 'react';
import { Shield, Phone, ArrowRight, AlertTriangle, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const AnxietyTreatmentPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Anxiety Treatment in Maryland"
        description="Anxiety treatment in Pikesville and Baltimore County, MD. SDORA Wellbeing provides outpatient therapy, medication management, and rehabilitation services for adults living with anxiety disorders. Accepting Medicaid."
        path="/resources/anxiety-treatment"
        type="article"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-700 to-teal-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Anxiety Treatment in Maryland
          </h1>
          <p className="text-lg text-emerald-200 max-w-2xl mx-auto">
            Anxiety is highly treatable. Our compassionate team helps you build the skills and support you need to manage worry, fear, and stress.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Understanding Anxiety */}
        <section className="mb-12" data-testid="anxiety-understanding-section">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Understanding Anxiety Disorders</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Everyone experiences worry or nervousness from time to time. But when anxiety becomes persistent, overwhelming,
            or interferes with your daily life, it may be an anxiety disorder. Anxiety disorders are among the most common
            mental health conditions, affecting millions of adults in the United States.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Common types include generalized anxiety disorder (GAD), social anxiety disorder, panic disorder, and specific phobias.
            Each has unique features, but they all share a pattern of excessive fear or worry that feels difficult to control.
            With the right treatment, most people experience significant relief.
          </p>
        </section>

        {/* Symptoms */}
        <section className="mb-12 bg-amber-50 rounded-xl p-6 border border-amber-100" data-testid="anxiety-symptoms">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Common Signs of Anxiety</h2>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            If several of these symptoms are affecting your work, relationships, or quality of life, professional support can help:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              "Persistent, excessive worry that is hard to control",
              "Feeling restless, on edge, or easily irritated",
              "Difficulty concentrating or mind going blank",
              "Muscle tension, headaches, or stomachaches",
              "Trouble falling or staying asleep",
              "Avoiding situations that trigger fear or panic",
              "Rapid heartbeat, sweating, or shortness of breath",
              "Feeling a sense of impending danger or dread",
            ].map((symptom) => (
              <div key={symptom} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
                {symptom}
              </div>
            ))}
          </div>
        </section>

        {/* Treatment Options */}
        <section className="mb-12" data-testid="anxiety-treatments">
          <h2 className="text-lg font-bold text-gray-900 mb-4">How SDORA Wellbeing Treats Anxiety</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Effective anxiety treatment often combines therapy, skill-building, and sometimes medication. Our team works with you
            to develop a treatment plan that addresses your specific type of anxiety and personal goals.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Individual Therapy (OMHC)",
                desc: "Licensed therapists use approaches like cognitive behavioral therapy (CBT) to help you identify and change thought patterns that fuel anxiety.",
                link: "/services/outpatient-mental-health",
              },
              {
                title: "Medication Management",
                desc: "When appropriate, psychiatric providers can prescribe and monitor medications that help reduce anxiety symptoms and support your progress in therapy.",
                link: "/services/medication-management",
              },
              {
                title: "Psychiatric Rehabilitation (PRP)",
                desc: "Hands-on support to help you practice coping skills, manage stressful situations, and rebuild confidence in everyday activities.",
                link: "/services/psychiatric-rehabilitation",
              },
              {
                title: "Comprehensive Assessment",
                desc: "A thorough evaluation to understand the nature and severity of your anxiety, identify co-occurring conditions, and guide treatment planning.",
                link: "/services/comprehensive-assessment",
              },
            ].map((item) => (
              <div key={item.title} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-900 text-sm mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{item.desc}</p>
                <Link to={item.link} className="text-sm text-emerald-600 font-medium hover:underline inline-flex items-center gap-1">
                  Learn more <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Self-Assessment */}
        <section className="mb-12 bg-emerald-50 rounded-xl p-6 border border-emerald-100" data-testid="anxiety-self-assessment">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Free Anxiety Self-Assessment</h2>
          <p className="text-sm text-gray-700 mb-3">
            Not sure if what you are experiencing is an anxiety disorder? Our free, confidential GAD-7 screening tool can help
            you understand your symptoms. It takes less than two minutes and is not a diagnosis — but it can be a useful starting point.
          </p>
          <Link to="/self-assessment" className="text-sm text-emerald-700 font-semibold hover:underline inline-flex items-center gap-1">
            Take the free screening <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* Telehealth */}
        <section className="mb-12 bg-blue-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-start gap-3 mb-3">
            <BookOpen className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Telehealth Available</h2>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            If leaving home triggers anxiety, telehealth can be a comfortable first step. Many of our anxiety treatment services
            are available via secure video sessions for eligible individuals in Maryland.
          </p>
          <Link to="/services/telehealth" className="text-sm text-blue-700 font-semibold hover:underline inline-flex items-center gap-1">
            Learn about telehealth options <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* Insurance */}
        <section className="mb-12 bg-teal-50 rounded-xl p-6 border border-teal-100">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Insurance & Coverage</h2>
          <p className="text-sm text-gray-700 mb-3">
            SDORA Wellbeing accepts Medicaid, Maryland HealthChoice managed care organizations, and most major insurance plans.
            Do not let cost be a barrier to getting help. Contact us to verify your coverage.
          </p>
          <Link to="/insurance" className="text-sm text-teal-700 font-semibold hover:underline inline-flex items-center gap-1">
            View accepted insurance plans <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* CTA */}
        <section className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200" data-testid="anxiety-cta">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Take the First Step Toward Relief</h2>
          <p className="text-sm text-gray-600 mb-4">
            You deserve to feel calmer and more in control. Call us today or submit a referral to begin your journey.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:+18777725150" className="inline-flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors">
              <Phone className="w-4 h-4" /> (877) 772-5150
            </a>
            <Link to="/get-started" className="inline-flex items-center gap-2 bg-white text-emerald-600 border border-emerald-200 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default AnxietyTreatmentPage;
